import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { BellOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Divider } from 'antd';

import { useMutation } from '@tanstack/react-query';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import {
  StoreCommunicationMessageLogActionSlug,
  StoreCommunicationMessageLogOrigin
} from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

function getTypeMessage(type: string | null) {
  if (type === 'billingReminder' || type === 'billingBankSlipReminder') {
    return (
      <>
        relacionadas a <strong>lembretes de vencimento de faturas de assinatura</strong>
      </>
    );
  }

  if (type === 'lateInvoice') {
    return (
      <>
        relacionadas a <strong>atraso de faturas de assinatura</strong>
      </>
    );
  }

  return null;
}

export function UnsubscribeWhatsappMessage() {
  const [unsubscribed, setUnsubscribed] = useState(false);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const sentMessageId = params.get('sentMessageId');
  const type = params.get('type');
  const blacklistMessage = useMutation({
    mutationFn: (sentMessageId: string) => subscriptionsService.blacklistMessage(sentMessageId),
    onSuccess: () => {
      setUnsubscribed(true);
      storeCommunicationMessageLog.mutate(sentMessageId);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.data?.message ||
        'Erro ao processar o descadastro. Por favor, tente novamente mais tarde.';
      toast.error(message);
    }
  });
  const storeCommunicationMessageLog = useMutation({
    mutationFn: (sentMessageId: string) =>
      subscriptionsService.storeCommunicationMessageLog({
        id: sentMessageId,
        actionSlug: StoreCommunicationMessageLogActionSlug.CLICKED,
        origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
        context: 'Descadastro lembrete de vencimento de fatura',
        createdAt: new Date().toISOString()
      }),
    onError: () => {
      toast.error('Erro ao processar o descadastro. Por favor, tente novamente mais tarde.');
    }
  });

  const typeMessage = getTypeMessage(type);

  if (!sentMessageId || !typeMessage) {
    return (
      <UnloggedLayout>
        <div className='mx-auto flex max-w-[480px] flex-col items-center justify-center px-4 pt-16 pb-8'>
          <div className='flex w-full flex-col items-center gap-4 border border-[rgba(0,0,0,0.12)] p-8 text-center'>
            <WarningOutlined className='mb-2 text-[#ff9800]!' style={{ fontSize: 72 }} />
            <h2>Link inválido</h2>
            <p>Parâmetros inválidos. Por favor, verifique o link recebido.</p>
          </div>
        </div>
      </UnloggedLayout>
    );
  }

  return (
    <UnloggedLayout>
      <div className='mx-auto flex max-w-[480px] flex-col items-center justify-center px-4 pt-16 pb-8'>
        <div className='flex w-full flex-col items-center gap-4 border border-[rgba(0,0,0,0.12)] p-8 text-center'>
          {unsubscribed ? (
            <>
              <h2 className='mb-0! text-base font-semibold'>Descadastro realizado com sucesso!</h2>
              <CheckCircleOutlined className='text-success! mb-2' style={{ fontSize: 72 }} />
              <p className='mb-0!'>Você não receberá mais mensagens {typeMessage} no WhatsApp.</p>
            </>
          ) : (
            <>
              <h2 className='mb-0! text-base font-semibold'>Você está quase lá!</h2>
              <p>Ao confirmar, você deixará de receber mensagens no WhatsApp {typeMessage} do MyEduzz.</p>
              <p>Este processo é irreversível e não será possível se cadastrar novamente no futuro.</p>
              <Button
                icon={<BellOutlined style={{ fontSize: 20 }} />}
                type='primary'
                className='mt-4 min-w-[180px] self-center'
                onClick={() => blacklistMessage.mutate(sentMessageId)}
                disabled={blacklistMessage.isPending}
                loading={blacklistMessage.isPending}
              >
                Confirmar descadastro
              </Button>
            </>
          )}
        </div>

        <div className='mt-8 flex w-full flex-col'>
          <div className='[&>a]:text-text flex items-center justify-center text-sm [&>a:hover]:underline'>
            <a href='https://www.eduzz.com/privacidade' target='_blank' rel='noopener noreferrer'>
              Política de privacidade
            </a>
            <Divider type='vertical' className='mx-2' />
            <a href='https://www.eduzz.com/termos' target='_blank' rel='noopener noreferrer'>
              Termos de compra
            </a>
            <Divider type='vertical' className='mx-2' />
            <a
              href='https://ajuda.eduzz.com/hc/pt-br/articles/1260806909249-Como-alterar-a-forma-de-pagamento-de-uma-assinatura'
              target='_blank'
              rel='noopener noreferrer'
            >
              Falar com suporte
            </a>
          </div>
        </div>
      </div>
    </UnloggedLayout>
  );
}
